import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiMoneyDollarCircleLine,
  RiBankCardLine,
  RiCalendarLine,
  RiFileTextLine,
  RiBarcodeLine,
  RiHistoryLine,
  RiWallet3Line,
  RiNewspaperLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/invoice-payment`;
// Adjust this to match wherever your invoice list endpoint actually lives
const INVOICE_BASE_URL = `${process.env.REACT_APP_API_URL}/invoice`;

const defaultForm = {
  amount: "",
  paymentMode: "cash",
  referenceNumber: "",
  paymentDate: "",
  remarks: "",
};

const defaultNewPaymentForm = {
  invoiceId: "",
  ...defaultForm,
};

const paymentModes = ["cash", "cheque", "neft", "rtgs", "upi", "card", "other"];

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, wide }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${
          wide ? "max-w-3xl" : "max-w-2xl"
        } mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const fieldIconMap = {
  amount: RiMoneyDollarCircleLine,
  paymentMode: RiBankCardLine,
  referenceNumber: RiBarcodeLine,
  paymentDate: RiCalendarLine,
  remarks: RiFileTextLine,
};

const Field = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  options,
}) => {
  const Icon = fieldIconMap[name] || RiFileTextLine;
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
        {options ? (
          <select
            name={name}
            value={value}
            onChange={onChange}
            className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none capitalize"
          >
            {options.map((opt) => (
              <option key={opt} value={opt} className="capitalize">
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder || label}
            className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
          />
        )}
      </div>
    </div>
  );
};

const paymentFormFields = [
  { label: "Amount", name: "amount", type: "number" },
  { label: "Payment Mode", name: "paymentMode", options: paymentModes },
  { label: "Reference Number", name: "referenceNumber" },
  { label: "Payment Date", name: "paymentDate", type: "date" },
  { label: "Remarks", name: "remarks" },
];

const PaymentFormBody = ({ form, handleChange }) => (
  <div className="grid grid-cols-2 gap-4 mb-4">
    {paymentFormFields.map((f) => (
      <div key={f.name} className={f.name === "remarks" ? "col-span-2" : ""}>
        <Field {...f} value={form[f.name]} onChange={handleChange} />
      </div>
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    unpaid: "bg-red-50 border-red-200 text-red-600",
    partially_paid: "bg-amber-50 border-amber-200 text-amber-600",
    paid: "bg-emerald-50 border-emerald-200 text-emerald-600",
  };
  const labels = {
    unpaid: "Unpaid",
    partially_paid: "Partially Paid",
    paid: "Paid",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border whitespace-nowrap ${
        styles[status] || styles.unpaid
      }`}
    >
      {labels[status] || status}
    </span>
  );
};

const formatCurrency = (val) =>
  `₹${Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDate = (val) =>
  val ? new Date(val).toLocaleDateString("en-IN") : "—";

// Payment.invoice.releaseOrder.client is a populated "Bank" doc. The exact
// field holding the display name isn't confirmed, so this checks the most
// likely candidates in order. Trim this list down once you know the real field.
const getClientName = (payment) => {
  const client = payment?.invoice?.releaseOrder?.client;
  if (!client) return "—";
  if (typeof client === "string") return "—"; // not populated
  return (
    client.name ||
    client.companyName ||
    client.bankName ||
    client.organisation ||
    client.accountHolderName ||
    "—"
  );
};

export default function AllInvoicePayments() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [entryForm, setEntryForm] = useState(defaultForm);
  const [newPaymentForm, setNewPaymentForm] = useState(defaultNewPaymentForm);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}`);
      setPayments(res.data?.data || []);
      return res.data?.data || [];
    } catch {
      notify("Failed to fetch payments", "error");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const res = await axios.get(`${INVOICE_BASE_URL}/all`);
      setInvoices(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch invoices", "error");
    } finally {
      setInvoicesLoading(false);
    }
  };

  const refreshSelected = async (id) => {
    const list = await fetchPayments();
    const updated = list.find((p) => p._id === id);
    if (updated) setSelectedPayment(updated);
    return updated;
  };

  const openAddPayment = (payment) => {
    setSelectedPayment(payment);
    setForm({
      ...defaultForm,
      paymentDate: new Date().toISOString().slice(0, 10),
    });
    setModal("addPayment");
  };
  const openNewPayment = () => {
    setNewPaymentForm({
      ...defaultNewPaymentForm,
      paymentDate: new Date().toISOString().slice(0, 10),
    });
    if (invoices.length === 0) fetchInvoices();
    setModal("newPayment");
  };
  const openHistory = (payment) => {
    setSelectedPayment(payment);
    setModal("history");
  };
  const openDeleteLedger = (payment) => {
    setSelectedPayment(payment);
    setModal("deleteLedger");
  };
  const openEditEntry = (entry) => {
    setSelectedEntry(entry);
    setEntryForm({
      amount: entry.amount ?? "",
      paymentMode: entry.paymentMode || "cash",
      referenceNumber: entry.referenceNumber || "",
      paymentDate: entry.paymentDate
        ? new Date(entry.paymentDate).toISOString().slice(0, 10)
        : "",
      remarks: entry.remarks || "",
    });
    setModal("editEntry");
  };
  const openDeleteEntry = (entry) => {
    setSelectedEntry(entry);
    setModal("deleteEntry");
  };

  const closeModal = () => {
    setModal(null);
    setSelectedPayment(null);
    setSelectedEntry(null);
    setNewPaymentForm(defaultNewPaymentForm);
  };
  const closeSubModal = () => {
    // return to history view instead of closing everything
    setSelectedEntry(null);
    setModal("history");
  };

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleEntryChange = (e) =>
    setEntryForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleNewPaymentChange = (e) =>
    setNewPaymentForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleAddPayment = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      notify("Enter a valid payment amount", "error");
      return;
    }
    const invoiceId = selectedPayment?.invoice?._id || selectedPayment?.invoice;
    setActionLoading(true);
    try {
      await axios.post(`${BASE_URL}/${invoiceId}/add`, form);
      notify("Payment added successfully");
      await fetchPayments();
      closeModal();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to add payment", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateNewPayment = async () => {
    if (!newPaymentForm.invoiceId) {
      notify("Select an invoice", "error");
      return;
    }
    if (!newPaymentForm.amount || Number(newPaymentForm.amount) <= 0) {
      notify("Enter a valid payment amount", "error");
      return;
    }
    const { invoiceId, ...payload } = newPaymentForm;
    setActionLoading(true);
    try {
      await axios.post(`${BASE_URL}/${invoiceId}/add`, payload);
      notify("Payment added successfully");
      await fetchPayments();
      closeModal();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to add payment", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateEntry = async () => {
    setActionLoading(true);
    try {
      await axios.put(
        `${BASE_URL}/${selectedPayment._id}/entry/${selectedEntry._id}`,
        entryForm,
      );
      notify("Payment entry updated");
      await refreshSelected(selectedPayment._id);
      closeSubModal();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to update entry", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEntry = async () => {
    setActionLoading(true);
    try {
      await axios.delete(
        `${BASE_URL}/${selectedPayment._id}/entry/${selectedEntry._id}`,
      );
      notify("Payment entry deleted");
      await refreshSelected(selectedPayment._id);
      closeSubModal();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to delete entry", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLedger = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/${selectedPayment._id}`);
      notify("Payment record deleted");
      fetchPayments();
      closeModal();
    } catch {
      notify("Failed to delete payment record", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = payments.filter((p) => {
    const clientName = getClientName(p);
    const matchesSearch =
      (p.invoiceNumber || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.invoice?.invoiceNumber || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      clientName.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const tableHeaders = [
    "Invoice No.",
    "Client",
    "Invoice Amount",
    "Amount Paid",
    "Remaining",
    "Status",
    "Last Payment",
    "Actions",
  ];

  // Derived info for the "Add Payment" (new) modal — resolves the picked
  // invoice's total and any remaining balance if a ledger already exists.
  const chosenInvoice = invoices.find(
    (inv) => inv._id === newPaymentForm.invoiceId,
  );
  const chosenExistingLedger = payments.find(
    (p) => (p.invoice?._id || p.invoice) === newPaymentForm.invoiceId,
  );
  const chosenTotal =
    chosenExistingLedger?.totalInvoiceAmount ?? chosenInvoice?.totalAmount ?? 0;
  const chosenRemaining =
    chosenExistingLedger?.remainingAmount ?? chosenInvoice?.totalAmount ?? 0;

  // Invoices that are already fully paid don't need to appear as pickable options
  const payableInvoices = invoices.filter((inv) => {
    const ledger = payments.find(
      (p) => (p.invoice?._id || p.invoice) === inv._id,
    );
    return !ledger || ledger.status !== "paid";
  });

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiWallet3Line className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Invoice Payments
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Payment Management
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            >
              <option value="all">All Statuses</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="paid">Paid</option>
            </select>
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search invoice no…"
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-52 transition"
              />
            </div>
            <button
              onClick={fetchPayments}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={openNewPayment}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Payment
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10">
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiWallet3Line className="w-4 h-4 text-blue-500" />
              All Invoice Payments
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {tableHeaders.map((label) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading payments…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiWallet3Line className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search || statusFilter !== "all"
                            ? "No payments match your filters."
                            : "No payment records found."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((p, i) => (
                    <tr
                      key={p._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                    >
                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {p.invoiceNumber || p.invoice?.invoiceNumber || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {getClientName(p)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {formatCurrency(p.totalInvoiceAmount)}
                      </td>
                      <td className="px-4 py-3 text-emerald-600 font-medium whitespace-nowrap">
                        {formatCurrency(p.amountPaid)}
                      </td>
                      <td className="px-4 py-3 text-red-500 font-medium whitespace-nowrap">
                        {formatCurrency(p.remainingAmount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {p.lastPaymentDate ? (
                          <div className="flex flex-col">
                            <span>{formatDate(p.lastPaymentDate)}</span>
                            <span className="text-[11px] text-blue-400 capitalize">
                              {p.lastPaymentMode}
                            </span>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 items-center">
                          <button
                            onClick={() => openAddPayment(p)}
                            disabled={p.status === "paid"}
                            title="Add Payment"
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <RiAddLine className="w-3.5 h-3.5" /> Add
                          </button>
                          <button
                            onClick={() => openHistory(p)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiHistoryLine className="w-3.5 h-3.5" /> History
                          </button>
                          <button
                            onClick={() => openDeleteLedger(p)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal === "newPayment" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Payment
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-1.5 mb-4">
            <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
              Invoice
            </label>
            <div className="relative">
              <RiNewspaperLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
              <select
                name="invoiceId"
                value={newPaymentForm.invoiceId}
                onChange={handleNewPaymentChange}
                className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
              >
                <option value="">
                  {invoicesLoading ? "Loading invoices…" : "Select an invoice"}
                </option>
                {payableInvoices.map((inv) => (
                  <option key={inv._id} value={inv._id}>
                    {inv.invoiceNumber} — {formatCurrency(inv.totalAmount)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {newPaymentForm.invoiceId && (
            <div className="mb-4 flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 text-sm">
              <span className="text-slate-500">
                Invoice Total:{" "}
                <span className="font-semibold text-slate-800">
                  {formatCurrency(chosenTotal)}
                </span>
              </span>
              <span className="text-slate-500">
                Remaining:{" "}
                <span className="font-semibold text-red-500">
                  {formatCurrency(chosenRemaining)}
                </span>
              </span>
            </div>
          )}

          <PaymentFormBody
            form={newPaymentForm}
            handleChange={handleNewPaymentChange}
          />

          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreateNewPayment}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Payment
            </button>
          </div>
        </Modal>
      )}

      {modal === "addPayment" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Payment —{" "}
              {selectedPayment?.invoiceNumber ||
                selectedPayment?.invoice?.invoiceNumber}
            </>
          }
          onClose={closeModal}
        >
          <div className="mb-4 flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 text-sm">
            <span className="text-slate-500">
              Remaining:{" "}
              <span className="font-semibold text-red-500">
                {formatCurrency(selectedPayment?.remainingAmount)}
              </span>
            </span>
            <StatusBadge status={selectedPayment?.status} />
          </div>
          <PaymentFormBody form={form} handleChange={handleChange} />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleAddPayment}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Payment
            </button>
          </div>
        </Modal>
      )}

      {modal === "history" && (
        <Modal
          title={
            <>
              <RiHistoryLine className="w-5 h-5 text-blue-500" /> Payment
              History — {selectedPayment?.invoiceNumber}
            </>
          }
          onClose={closeModal}
          wide
        >
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-[11px] uppercase text-blue-400 font-semibold">
                Invoice Total
              </p>
              <p className="text-sm font-bold text-slate-800">
                {formatCurrency(selectedPayment?.totalInvoiceAmount)}
              </p>
            </div>
            <div className="px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <p className="text-[11px] uppercase text-emerald-500 font-semibold">
                Paid
              </p>
              <p className="text-sm font-bold text-emerald-700">
                {formatCurrency(selectedPayment?.amountPaid)}
              </p>
            </div>
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-100">
              <p className="text-[11px] uppercase text-red-400 font-semibold">
                Remaining
              </p>
              <p className="text-sm font-bold text-red-600">
                {formatCurrency(selectedPayment?.remainingAmount)}
              </p>
            </div>
          </div>

          {!selectedPayment?.paymentsMade ||
          selectedPayment.paymentsMade.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              No payments recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto border border-blue-100 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50/50 border-b border-blue-100">
                    {["Date", "Amount", "Mode", "Reference", "Remarks", ""].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {[...selectedPayment.paymentsMade]
                    .sort(
                      (a, b) =>
                        new Date(b.paymentDate) - new Date(a.paymentDate),
                    )
                    .map((entry) => (
                      <tr
                        key={entry._id}
                        className="border-b border-blue-50 hover:bg-blue-50/40 transition"
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                          {formatDate(entry.paymentDate)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-semibold text-slate-800">
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap capitalize text-slate-500">
                          {entry.paymentMode}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                          {entry.referenceNumber || "—"}
                        </td>
                        <td className="px-3 py-2 max-w-[160px] truncate text-slate-500">
                          {entry.remarks || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditEntry(entry)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition"
                            >
                              <RiEditLine className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openDeleteEntry(entry)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 transition"
                            >
                              <RiDeleteBinLine className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Close
            </button>
          </div>
        </Modal>
      )}

      {modal === "editEntry" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Payment
              Entry
            </>
          }
          onClose={closeSubModal}
        >
          <PaymentFormBody form={entryForm} handleChange={handleEntryChange} />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeSubModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdateEntry}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {modal === "deleteEntry" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Delete
              Payment Entry
            </>
          }
          onClose={closeSubModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete payment of{" "}
              <span className="text-red-500">
                {formatCurrency(selectedEntry?.amount)}
              </span>
              ?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This will recalculate the remaining amount and status for this
              invoice.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeSubModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDeleteEntry}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Entry
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "deleteLedger" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete payment record for{" "}
              <span className="text-red-500">
                {selectedPayment?.invoiceNumber}
              </span>
              ?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This removes the entire payment ledger and history for this
              invoice. This action cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDeleteLedger}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Record
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
